// DishViewer.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  IconButton,
} from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useCart } from '../../contexts/CartContext';
import dataService from '../../services/dataService';


const DishViewer = ({ dishes, restaurantId }) => {
    const [images, setImages] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedDish, setSelectedDish] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [specialRequests, setSpecialRequests] = useState('');
    const [price, setPrice] = useState('');
    const { addItem, setRestaurantId } = useCart();

    useEffect(() => {
        const fetchImages = async () => {
            setImages([])
            for (let i = 0; i < dishes.length; i++) {
                console.log('Index', i, dishes[i])
                let dish = dishes[i];
                let imageUrl;
                try {
                    imageUrl = await dataService.genImageDish(restaurantId, dish);
                } catch {
                    imageUrl = null;
                }
                // Update state with each new image
                console.log('Pushing', 42069)
                setImages((imgs) => [...imgs, imageUrl]);
            }
        };
        fetchImages();
    }, [dishes]);

    const handleAddToCart = (dishName) => {
        setSelectedDish(dishName);
        setQuantity(1);
        setSpecialRequests('');
        setPrice('');
        setDialogOpen(true);
    };

    const handleConfirmAdd = () => {
        if (!price || parseFloat(price) <= 0) {
            alert('Please enter a valid price');
            return;
        }

        const priceInCents = Math.round(parseFloat(price) * 100);

        // Add item to cart
        addItem({
            id: `${restaurantId}-${selectedDish}-${Date.now()}`,
            menu_item_id: null, // Will be resolved later or on backend
            item_name: selectedDish,
            item_description: `From ${restaurantId}`,
            quantity: quantity,
            unit_price_cents: priceInCents,
            special_requests: specialRequests || null,
        });

        // Set restaurant ID for the cart
        setRestaurantId(restaurantId);

        setDialogOpen(false);
        setSelectedDish(null);
    };

    return (
        <>
            <Box sx={{ display: 'flex', overflowX: 'auto', p: 2, gap: 2 }}>
                {dishes.map((dish, index) => {
                    const hasImage = images.length > index && images[index] && images[index]['img_base64'];

                    if (!hasImage && images.length <= index) {
                        // Still loading
                        return <CircularProgress key={index} />;
                    }

                    if (!hasImage) {
                        // Image generation disabled or failed - show dish name with button
                        return (
                            <Box key={index} sx={{ textAlign: 'center', mr: 2, minWidth: 150 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>{dish}</Typography>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<AddShoppingCartIcon />}
                                    onClick={() => handleAddToCart(dish)}
                                    fullWidth
                                >
                                    Add to Cart
                                </Button>
                            </Box>
                        );
                    }

                    return (
                        <Box key={index} sx={{ textAlign: 'center', mr: 2, minWidth: 150 }}>
                            <img
                                src={images[index]['img_base64']}
                                alt={dish}
                                style={{
                                    width: '100%',
                                    minWidth: 150,
                                    height: 'auto',
                                    borderRadius: 8,
                                    marginBottom: 8
                                }}
                            />
                            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>{dish}</Typography>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddShoppingCartIcon />}
                                onClick={() => handleAddToCart(dish)}
                                fullWidth
                            >
                                Add
                            </Button>
                        </Box>
                    );
                })}
            </Box>

            {/* Add to Cart Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add {selectedDish} to Cart</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        <TextField
                            label="Price"
                            type="number"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            required
                            fullWidth
                            inputProps={{ min: 0, step: 0.01 }}
                            helperText="Enter the price from the menu"
                            placeholder="e.g., 12.99"
                        />

                        <Box>
                            <Typography variant="body2" gutterBottom>
                                Quantity
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton
                                    size="small"
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                    disabled={quantity <= 1}
                                >
                                    <RemoveIcon />
                                </IconButton>
                                <TextField
                                    value={quantity}
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value) || 1;
                                        setQuantity(Math.max(1, val));
                                    }}
                                    inputProps={{ min: 1, max: 99, style: { textAlign: 'center' } }}
                                    sx={{ width: 80 }}
                                    size="small"
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => setQuantity(quantity + 1)}
                                >
                                    <AddIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        <TextField
                            label="Special Requests (Optional)"
                            value={specialRequests}
                            onChange={(e) => setSpecialRequests(e.target.value)}
                            multiline
                            rows={2}
                            fullWidth
                            placeholder="e.g., No onions, extra spicy"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleConfirmAdd}>
                        Add to Cart
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default DishViewer;
